import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faClock, faComments, faPhone } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import { validatePhone } from '../../utils/helpers';
import './ContactForm.css';

const ContactForm = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        service: '',
        message: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const baseUrl = useMemo(
        () => import.meta.env.VITE_API_BASE_URL?.trim()?.replace(/\/+$/, '') || '',
        []
    );
    const endpoint = useMemo(
        () => `${baseUrl}/api/v1/contacts/submit/`,
        [baseUrl]
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const serviceOptions = [
        { value: 'diagnosis', label: t('contact.form.service_options.diagnosis') },
        { value: 'consultation', label: t('contact.form.service_options.consultation') },
        // { value: 'insoles', label: t('contact.form.service_options.insoles') },
        // { value: 'other', label: t('contact.form.service_options.other') },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = formData.name.trim();
        const phone = formData.phone.trim();
        
        const serviceValue = formData.service.trim();
        const selectedServiceOption = serviceOptions.find(opt => opt.value === serviceValue);
        const service = selectedServiceOption ? selectedServiceOption.label : serviceValue;
        
        const message = formData.message.trim();

        if (!name || !phone) {
            setStatus({
                type: 'error',
                message: t('contact.form.required_error', {
                    defaultValue: "Iltimos, ism va telefon raqamini kiriting."
                })
            });
            return;
        }
        if (!validatePhone(phone)) {
            setStatus({
                type: 'error',
                message: t('contact.form.phone_error', {
                    defaultValue: "Telefon raqam noto'g'ri kiritildi."
                })
            });
            return;
        }
        if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
            console.error('Blocked by mixed-content policy. Use HTTPS API URL for HTTPS site.', { baseUrl });
            setStatus({
                type: 'error',
                message: t('contact.form.mixed_content_error', {
                    defaultValue: "Xavfsizlik xatosi: HTTPS saytdan HTTP API'ga yuborib bo'lmaydi. API URL'ni HTTPS qiling."
                })
            });
            return;
        }

        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            const payload = {
                name,
                phone,
                ...(service ? { service } : {}),
                ...(message ? { message } : {})
            };
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const errorBody = await response.text().catch(() => '');
                throw new Error(
                    `Contacts submit request failed: ${response.status}${errorBody ? ` - ${errorBody}` : ''}`
                );
            }
            setStatus({ type: 'success', message: t('contact.form.success') });
            setFormData({ name: '', phone: '', service: '', message: '' });
        } catch (error) {
            console.error(error.message || 'Failed to submit contact form', { endpoint });
            const networkLikeError = error?.name === 'TypeError' || /Failed to fetch/i.test(error?.message || '');
            setStatus({
                type: 'error',
                message: networkLikeError
                    ? t('contact.form.network_error', {
                        defaultValue: "So'rov yuborilmadi. Internet, CORS yoki API URL'ni tekshiring."
                    })
                    : t('contact.form.error')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact-form" className="section contact-form-section">
            <div className="container">
                <div className="contact-form-wrapper">
                    {/* Info Side */}
                    <motion.div
                        className="contact-info-side"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <h2 className="contact-title">{t('contact.title')}</h2>
                        <p className="contact-subtitle">{t('contact.subtitle')}</p>

                        <div className="contact-features">
                            <div className="contact-feature">
                                <span className="feature-emoji" aria-hidden="true">
                                    <FontAwesomeIcon icon={faPhone} />
                                </span>
                                <span>{t('contact.features.quick_response')}</span>
                            </div>
                            <div className="contact-feature">
                                <span className="feature-emoji" aria-hidden="true">
                                    <FontAwesomeIcon icon={faComments} />
                                </span>
                                <span>{t('contact.features.via_telegram')}</span>
                            </div>
                            <div className="contact-feature">
                                <span className="feature-emoji" aria-hidden="true">
                                    <FontAwesomeIcon icon={faClock} />
                                </span>
                                <span>{t('contact.features.within_24_hours')}</span>
                            </div>
                        </div>

                        <div className="contact-decoration">
                            <span className="deco-icon">
                                <img src="/logo.jpg" alt="" width="120" height="120" loading="lazy" decoding="async" />
                            </span>
                        </div>
                    </motion.div>

                    {/* Form Side */}
                    <motion.div
                        className="contact-form-side"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-group">
                                <label htmlFor="name" className="form-label">
                                    {t('contact.form.name')} <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('contact.form.name_placeholder')}
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone" className="form-label">
                                    {t('contact.form.phone')} <span className="required">*</span>
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder={t('contact.form.phone_placeholder')}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="service" className="form-label">
                                    {t('contact.form.service')}
                                </label>
                                <select
                                    id="service"
                                    name="service"
                                    value={formData.service}
                                    onChange={handleChange}
                                    className="form-input form-select"
                                >
                                    <option value="">{t('contact.form.service_placeholder')}</option>
                                    {serviceOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message" className="form-label">{t('contact.form.message')}</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder={t('contact.form.message_placeholder')}
                                    className="form-input form-textarea"
                                    rows="4"
                                />
                            </div>

                            {status.message && (
                                <div className={`form-status ${status.type}`}>
                                    <span className="form-status-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={status.type === 'success' ? faCircleCheck : faCircleXmark} />
                                    </span>
                                    {status.message}
                                </div>
                            )}

                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                fullWidth
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '...' : t('contact.form.submit')}
                            </Button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default ContactForm;
